import { Pencil } from "lucide-react";
import { useState } from "react";
import { CellContext } from "@tanstack/react-table";
import { WeeklyLimitPopover } from "../../weekly-limit-popover";
import type { WeeklyLimitVariant } from "../../weekly-limit-popover";
import { VariantWithProductData } from "../types";
import { setWeeklyLimitCellTestId } from "../products-table.utils";
import { useProductsState } from "@client-shared/providers/products";
import { useVendorContext } from "@client-shared/providers/vendor";
import { EmptyValue } from "@bcc/components/ui/empty-value";
import { resolveShipToInventory } from "@bcc/lib/products/ship-to-countries";

interface WeeklyLimitCellProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const WeeklyLimitCellMarkup = (props: CellContext<VariantWithProductData, unknown> & WeeklyLimitCellProps) => {
  const data = props.row.original;
  const [, , productsProviderMethods] = useProductsState();
  const [{ currentVendor }] = useVendorContext();

  // One-shot lockout: once the popover reports a successful submit we
  // lock this row's trigger so the user can't fire a second duplicate
  // approval request. Resets on page refresh (component unmount).
  const [hasSubmitted, setHasSubmitted] = useState(false);

  if (!data.skuCode || !currentVendor) {
    return (
      <span data-testid={setWeeklyLimitCellTestId(props.row.index)}>
        <EmptyValue />
      </span>
    );
  }

  // SS rows from getServiceProducts can populate `maxBagsShippedPerWeek` only;
  // Standard PIM-first rows prefer PIM `weeklyInventory`, with SF as fallback.
  // Fall back so the popover and the displayed number never diverge.
  const fallbackWeeklyLimit = data.weeklyInventory ?? data.maxBagsShippedPerWeek ?? 0;
  // Cross-border SKUs carry a per-country inventory in `shipToInfo`. Show the
  // logged-in country's `inventoryQuantity` (which reflects an approved Inventory
  // Update), falling back to the single-valued figure for non-cross-border rows.
  const weeklyLimit = resolveShipToInventory(data.shipToInfo, currentVendor.countryCode, fallbackWeeklyLimit);
  const variant: WeeklyLimitVariant = {
    id: data.id,
    size: data.bagSize ?? "",
    weeklyLimit,
    sku: data.skuCode,
    vendorKey: currentVendor.vendorKey,
    productName: data.productTitle ?? "",
    coffeeTerm: data.term === "Short" ? "Short" : "Long",
    roasterCountryCode: currentVendor.countryCode,
    shipToInfo: data.shipToInfo,
    shipTo: data.shipTo?.map((country) => country.countryCode),
  };

  const isDisabled = !currentVendor?.vendorKey || !productsProviderMethods || !!data.hasPendingWeeklyLimitApproval;

  const handleUpdateWeeklyLimit =
    productsProviderMethods?.handleUpdateWeeklyLimit ?? (() => Promise.reject(new Error("Products provider not available")));

  return (
    <div data-testid={setWeeklyLimitCellTestId(props.row.index)}>
      {hasSubmitted ? (
        <button type="button" disabled className="inline-flex items-center gap-1 text-sm text-primary opacity-50 cursor-not-allowed">
          {weeklyLimit}
          <Pencil className="h-3 w-3" />
        </button>
      ) : (
        <WeeklyLimitPopover
          variant={variant}
          onUpdateWeeklyLimit={handleUpdateWeeklyLimit}
          isUpdating={productsProviderMethods?.isUpdatingWeeklyLimit}
          onSubmitSuccess={() => setHasSubmitted(true)}
          open={props.open}
          onOpenChange={props.onOpenChange}
        >
          <button
            type="button"
            disabled={isDisabled}
            className="inline-flex items-center gap-1 text-sm text-primary hover:underline cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {weeklyLimit}
            <Pencil className="h-3 w-3" />
          </button>
        </WeeklyLimitPopover>
      )}
    </div>
  );
};
